import { createInertiaApp, router } from '@inertiajs/react'
import { frFR } from '@clerk/localizations'
import { ClerkProvider } from '@clerk/react'

import AppLayout from '@/layouts/app_layout'

// Only the publishable key reaches the browser (VITE_ prefix). The secret key
// stays server-side, always.
const clerkPublishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as
  string | undefined

if (!clerkPublishableKey) {
  throw new Error(
    'VITE_CLERK_PUBLISHABLE_KEY is missing: run `npx -y clerk@latest env pull --file .env` (see .env.example).',
  )
}

void createInertiaApp({
  pages: '../pages',

  serverHead: true,

  title: (title) => (title ? `${title} · indeck` : 'indeck'),

  strictMode: true,

  // Default layout applied to every page.
  layout: () => AppLayout,

  // ClerkProvider wraps the whole Inertia app. Clerk's multi-step pages
  // (/sign-in/factor-one…) navigate through Inertia, like any other page;
  // the state is kept so <SignIn /> is not remounted between two steps.
  withApp: (app) => (
    <ClerkProvider
      publishableKey={clerkPublishableKey}
      localization={frFR}
      signInUrl="/sign-in"
      signUpUrl="/sign-up"
      signInFallbackRedirectUrl="/dashboard"
      signUpFallbackRedirectUrl="/dashboard"
      afterSignOutUrl="/"
      routerPush={(to) =>
        router.visit(to, { preserveState: true, preserveScroll: true })
      }
      routerReplace={(to) =>
        router.visit(to, {
          replace: true,
          preserveState: true,
          preserveScroll: true,
        })
      }
    >
      {app}
    </ClerkProvider>
  ),

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
      withAllErrors: true,
    },
    visitOptions: () => {
      return { queryStringArrayFormat: 'brackets' }
    },
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  if (document.getElementById('app')) {
    throw error
  } else {
    console.error(
      'Missing root element.\n\n' +
        'If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n' +
        'Consider moving <%= vite_typescript_tag "inertia.tsx" %> to the Inertia-specific layout instead.',
    )
  }
})
